import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/components/ui/dialog';
import { Button } from '../../../ui/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../ui/components/ui/form';
import { Input } from '../../../ui/components/ui/input';
import { Textarea } from '../../../ui/components/ui/textarea';
import { BannerNotificationListFullResponseDto } from '../types/response';

const bannerFormSchema = z
  .object({
    message: z.string().min(1, 'Nachricht ist erforderlich'),
    dateStart: z.string().min(1, 'Startdatum ist erforderlich'),
    dateEnd: z.string().min(1, 'Enddatum ist erforderlich')
  })
  .refine(
    (data) => {
      const start = new Date(`${data.dateStart}T00:00:00`);
      const end = new Date(`${data.dateEnd}T00:00:00`);
      return start <= end;
    },
    {
      message: 'Startdatum darf nicht nach Enddatum liegen',
      path: ['dateEnd']
    }
  );

type BannerFormValues = z.infer<typeof bannerFormSchema>;

interface BannerFormDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: { message: string; dateStart: string; dateEnd: string }) => Promise<void>;
  banner?: BannerNotificationListFullResponseDto | null;
}

const convertToUTCISO = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(Number);

  const utcDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

  return utcDate.toISOString();
};

const convertFromUTCISO = (isoString: string): string => {
  const date = new Date(isoString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const BannerFormDialog = ({ isOpen, onOpenChange, onSubmit, banner }: BannerFormDialogProps) => {
  const form = useForm<BannerFormValues>({
    resolver: zodResolver(bannerFormSchema),
    defaultValues: {
      message: '',
      dateStart: '',
      dateEnd: ''
    }
  });

  // Load banner data when editing
  useEffect(() => {
    if (banner && isOpen) {
      const startDate = convertFromUTCISO(banner.dateStart);
      const endDate = convertFromUTCISO(banner.dateEnd);
      form.reset({
        message: banner.message,
        dateStart: startDate,
        dateEnd: endDate
      });
    } else if (!banner && isOpen) {
      // Reset form for create mode
      form.reset({
        message: '',
        dateStart: '',
        dateEnd: ''
      });
    }
  }, [banner, isOpen, form]);

  const handleSubmit = async (values: BannerFormValues) => {
    const dateStart = convertToUTCISO(values.dateStart);
    const dateEnd = convertToUTCISO(values.dateEnd);

    await onSubmit({
      message: values.message,
      dateStart,
      dateEnd
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md min-w-[560px] py-4 px-6 gap-5 bg-[#ffffff]" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-[25px] font-bold">{banner ? 'Banner bearbeiten' : 'Banner erstellen'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-5">
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[14px]">
                  <FormLabel className="text-[16px] font-bold text-[#12282A]">Nachricht:*</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Nachricht eingeben" className="resize-none min-h-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <FormField
                  control={form.control}
                  name="dateStart"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-[16px] font-bold text-[#12282A]">Startdatum:*</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" placeholder="Startdatum" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dateEnd"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-[16px] font-bold text-[#12282A]">Enddatum:*</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" placeholder="Enddatum" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit">{banner ? 'Speichern' : 'Erstellen'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BannerFormDialog;
